const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

export const api = {
  // Installation
  async checkInstallation() {
    const response = await fetch(`${API_BASE}/installation/status`);
    return response.json();
  },

  async setupInstallation(data) {
    const response = await fetch(`${API_BASE}/installation/setup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Installation failed');
    }
    return response.json();
  },

  // Authentication
  async login(credentials) {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }
    return response.json();
  },

  async verifyOTP(data) {
    const response = await fetch(`${API_BASE}/auth/verify-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'OTP verification failed');
    }
    return response.json();
  },

  async forgotPassword(email) {
    const response = await fetch(`${API_BASE}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Request failed');
    }
    return response.json();
  },

  async resetPassword(data) {
    const response = await fetch(`${API_BASE}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Password reset failed');
    }
    return response.json();
  },

  async logout() {
    const response = await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return response.json();
  },

  async checkSession() {
    const response = await fetch(`${API_BASE}/auth/check-session`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Session expired');
    return response.json();
  },

  // Admin - Staff
  async createStaff(data) {
    const response = await fetch(`${API_BASE}/admin/staff`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create staff');
    }
    return response.json();
  },

  async getStaff() {
    const response = await fetch(`${API_BASE}/admin/staff`, {
      headers: getAuthHeaders()
    });
    return response.json();
  },

  async updateStaff(data) {
    const response = await fetch(`${API_BASE}/admin/staff`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update staff');
    }
    return response.json();
  },

  async generateStaffOTP(staffId) {
    const response = await fetch(`${API_BASE}/admin/staff/generate-otp`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ staff_id: staffId })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to generate OTP');
    }
    return response.json();
  },

  // Admin - Campaigns
  async createCampaign(data) {
    const response = await fetch(`${API_BASE}/admin/campaigns`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create campaign');
    }
    return response.json();
  },

  async getCampaigns() {
    const response = await fetch(`${API_BASE}/admin/campaigns`, {
      headers: getAuthHeaders()
    });
    return response.json();
  },

  async updateCampaign(data) {
    const response = await fetch(`${API_BASE}/admin/campaigns`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update campaign');
    }
    return response.json();
  },

  async getCampaignCSVUrl(campaignId) {
    const response = await fetch(`${API_BASE}/admin/campaigns/${campaignId}/csv-url`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get CSV URL');
    }
    return response.json();
  },

  // Admin - Activity Logs
  async getActivityLogs(skip = 0, limit = 100) {
    const response = await fetch(`${API_BASE}/admin/activity-logs?skip=${skip}&limit=${limit}`, {
      headers: getAuthHeaders()
    });
    return response.json();
  },

  // Admin - SMTP
  async updateSMTP(data) {
    const response = await fetch(`${API_BASE}/admin/smtp`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update SMTP');
    }
    return response.json();
  },

  async getSMTP() {
    const response = await fetch(`${API_BASE}/admin/smtp`, {
      headers: getAuthHeaders()
    });
    return response.json();
  },

  async testSMTP() {
    const response = await fetch(`${API_BASE}/admin/smtp/test`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Test email failed');
    }
    return response.json();
  },

  // Admin - Email Templates
  async getEmailTemplates() {
    const response = await fetch(`${API_BASE}/admin/email-templates`, {
      headers: getAuthHeaders()
    });
    return response.json();
  },

  async updateEmailTemplate(data) {
    const response = await fetch(`${API_BASE}/admin/email-templates`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update template');
    }
    return response.json();
  },

  // Admin - Trusted Devices
  async getAllTrustedDevices() {
    const response = await fetch(`${API_BASE}/admin/trusted-devices`, {
      headers: getAuthHeaders()
    });
    return response.json();
  },

  async removeTrustedDeviceAdmin(deviceId) {
    const response = await fetch(`${API_BASE}/admin/trusted-devices/${deviceId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to remove device');
    }
    return response.json();
  },

  // Staff - Campaigns
  async getStaffCampaigns() {
    const response = await fetch(`${API_BASE}/staff/campaigns`, {
      headers: getAuthHeaders()
    });
    return response.json();
  },

  async addManualLead(data) {
    const response = await fetch(`${API_BASE}/staff/leads/manual`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = 'Failed to add lead';
      try {
        const error = JSON.parse(errorText);
        errorMessage = error.detail || errorMessage;
      } catch (e) {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }
    return response.json();
  },

  async uploadBulkLeads(campaignId, file) {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE}/staff/leads/bulk?campaign_id=${campaignId}`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` })
      },
      body: formData
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to upload leads');
    }
    return response.json();
  },

  async confirmBulkLeads(campaignId, leads) {
    const response = await fetch(`${API_BASE}/staff/leads/bulk/confirm?campaign_id=${campaignId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(leads)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to confirm leads');
    }
    return response.json();
  },

  // Staff - Activity Logs
  async getStaffActivityLogs(skip = 0, limit = 100) {
    const response = await fetch(`${API_BASE}/staff/activity-logs?skip=${skip}&limit=${limit}`, {
      headers: getAuthHeaders()
    });
    return response.json();
  },

  // Staff - Trusted Devices
  async getStaffTrustedDevices() {
    const response = await fetch(`${API_BASE}/staff/trusted-devices`, {
      headers: getAuthHeaders()
    });
    return response.json();
  },

  async removeStaffTrustedDevice(deviceId) {
    const response = await fetch(`${API_BASE}/staff/trusted-devices/${deviceId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to remove device');
    }
    return response.json();
  },

  // Staff - Change Password
  async changePassword(data) {
    const response = await fetch(`${API_BASE}/staff/change-password`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to change password');
    }
    return response.json();
  }
};
